import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface CidadaoMetadados {
  id: string;
  nome: string;
  nome_social: string | null;
  email: string;
  cpf: string | null;
  cnpj: string | null;
  telefone: string | null;
  whatsapp: string | null;
  data_nascimento: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  email_verificado: boolean;
  cpf_verificado: boolean;
  cadastro_completo: boolean;
  campos_customizados?: Record<string, string> | null;
}

interface AuthContextValue {
  session: Session | null;
  cidadao: CidadaoMetadados | null;
  loading: boolean;
  cadastroCompleto: boolean;
  signIn: (email: string, senha: string, captchaToken?: string) => Promise<{ error: string | null }>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signUp: (dados: { nome: string; nome_social?: string; email: string; cpf?: string; cnpj?: string; senha: string; captchaToken?: string; campos_customizados?: Record<string, string> }) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  recuperarSenha: (email: string, captchaToken?: string) => Promise<{ error: string | null }>;
  recarregarCidadao: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [cidadao, setCidadao] = useState<CidadaoMetadados | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session) {
        carregarCidadao(data.session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (newSession) {
        carregarCidadao(newSession.user.id);
      } else {
        setCidadao(null);
        setLoading(false);
      }
    });

    return () => subscription.subscription.unsubscribe();
  }, []);

  async function carregarCidadao(userId: string) {
    try {
      const { data, error } = await supabase
        .from('cidadaos')
        .select('id, nome, nome_social, email, cpf, cnpj, telefone, whatsapp, data_nascimento, cep, logradouro, numero, complemento, bairro, cidade, uf, email_verificado, cpf_verificado, campos_customizados')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Erro ao carregar cidadão:', error);
        setCidadao(null);
      } else if (data) {
        const c = data as Record<string, string | boolean | null>;
        setCidadao({
          id: c.id as string,
          nome: c.nome as string,
          nome_social: (c.nome_social as string | null) ?? null,
          email: c.email as string,
          cpf: c.cpf as string | null,
          cnpj: c.cnpj as string | null,
          telefone: c.telefone as string | null,
          whatsapp: c.whatsapp as string | null,
          data_nascimento: c.data_nascimento as string | null,
          cep: c.cep as string | null,
          logradouro: c.logradouro as string | null,
          numero: c.numero as string | null,
          complemento: c.complemento as string | null,
          bairro: c.bairro as string | null,
          cidade: c.cidade as string | null,
          uf: c.uf as string | null,
          email_verificado: !!c.email_verificado,
          cpf_verificado: !!c.cpf_verificado,
          cadastro_completo: !!(c.cpf || c.cnpj) && !!c.telefone && !!c.cidade,
        });
      } else {
        // Logado, mas ainda sem perfil em `cidadaos` (ex.: 1º login via Google).
        // Não criamos a linha aqui porque o banco exige cpf OU cnpj (constraint
        // cidadaos_cpf_ou_cnpj). O usuário é levado a /completar-cadastro, que cria
        // a linha já com o CPF. RotaProtegida cuida do redirecionamento.
        setCidadao(null);
      }
    } finally {
      setLoading(false);
    }
  }

  async function signIn(email: string, senha: string, captchaToken?: string): Promise<{ error: string | null }> {
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: senha,
      options: captchaToken ? { captchaToken } : undefined,
    });
    if (error) {
      setLoading(false);
      return { error: traduzirErroAuth(error.message) };
    }
    return { error: null };
  }

  async function signInWithGoogle(): Promise<{ error: string | null }> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) return { error: traduzirErroAuth(error.message) };
    return { error: null };
  }

  async function signUp(dados: { nome: string; nome_social?: string; email: string; cpf?: string; cnpj?: string; senha: string; captchaToken?: string; campos_customizados?: Record<string, string> }): Promise<{ error: string | null }> {
    setLoading(true);
    // O registro em `cidadaos` é criado server-side pelo trigger on_auth_cidadao_created
    // a partir destes metadados — funciona mesmo com confirmação de e-mail (sem sessão) e via Google.
    const { data: signUpData, error } = await supabase.auth.signUp({
      email: dados.email,
      password: dados.senha,
      options: {
        data: {
          nome: dados.nome,
          nome_social: dados.nome_social?.trim() || null,
          cpf: dados.cpf?.replace(/\D/g, '') || null,
          cnpj: dados.cnpj?.replace(/\D/g, '') || null,
          origem_cadastro: 'portal',
          ...(dados.campos_customizados && Object.keys(dados.campos_customizados).length > 0
            ? { campos_customizados: dados.campos_customizados }
            : {}),
        },
        ...(dados.captchaToken ? { captchaToken: dados.captchaToken } : {}),
      },
    });
    if (error) {
      setLoading(false);
      return { error: traduzirErroAuth(error.message) };
    }
    if (!signUpData.user) {
      setLoading(false);
      return { error: 'Não foi possível criar a conta.' };
    }
    return { error: null };
  }

  async function signOut(): Promise<void> {
    await supabase.auth.signOut();
    setSession(null);
    setCidadao(null);
  }

  async function recuperarSenha(email: string, captchaToken?: string): Promise<{ error: string | null }> {
    const redirectTo = `${window.location.origin}/redefinir-senha`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo, ...(captchaToken ? { captchaToken } : {}) });
    if (error) return { error: traduzirErroAuth(error.message) };
    return { error: null };
  }

  async function recarregarCidadao() {
    if (session?.user?.id) await carregarCidadao(session.user.id);
  }

  return (
    <AuthContext.Provider value={{
      session,
      cidadao,
      loading,
      cadastroCompleto: cidadao?.cadastro_completo ?? false,
      signIn,
      signInWithGoogle,
      signUp,
      signOut,
      recuperarSenha,
      recarregarCidadao,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  return ctx;
}

function traduzirErroAuth(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes('invalid login credentials')) return 'E-mail ou senha incorretos.';
  if (m.includes('user already registered')) return 'Este e-mail já está cadastrado. Faça login.';
  if (m.includes('email not confirmed')) return 'Confirme seu e-mail antes de entrar.';
  if (m.includes('password should be at least')) return 'A senha precisa de no mínimo 6 caracteres.';
  if (m.includes('rate limit')) return 'Muitas tentativas. Aguarde alguns minutos.';
  if (m.includes('captcha')) return 'A verificação de segurança expirou. Recarregue a página e tente novamente.';
  return msg;
}
